import { getToken, onMessage } from 'firebase/messaging';
import { messaging } from '../firebase';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

/**
 * Request browser notification permission.
 * Returns true if granted.
 */
export const requestNotificationPermission = async () => {
  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    logger.error('Error requesting notification permission:', error);
    return false;
  }
};

/**
 * Save an FCM device token to the user's Firestore profile.
 * Uses arrayUnion so multiple devices are tracked per user.
 */
export const saveFCMToken = async (userId, token) => {
  try {
    if (!userId || !token) return false;
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      fcmTokens: arrayUnion(token),
      lastTokenUpdate: new Date(),
    });
    return true;
  } catch (error) {
    logger.error('Error saving FCM token:', error);
    return false;
  }
};

/**
 * Full FCM setup flow:
 * 1. Check browser support
 * 2. Request permission
 * 3. Get/refresh token with VAPID key
 * 4. Save token to Firestore
 *
 * Returns true on success, false if unsupported/denied/failed.
 */
export const setupNotifications = async (userId) => {
  if (!userId) return false;

  // messaging is null when the browser doesn't support FCM
  if (!messaging) {
    logger.warn('FCM: messaging not available in this browser');
    return false;
  }

  const isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  if (!isSupported) {
    logger.warn('FCM: push notifications not supported in this browser');
    return false;
  }

  try {
    const granted = await requestNotificationPermission();
    if (!granted) {
      // User denied or dismissed the browser prompt — not an error
      return false;
    }

    // Register the service worker explicitly so FCM can use it
    const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });

    if (!token) {
      logger.warn('FCM: no token returned (VAPID key missing?)');
      return false;
    }

    await saveFCMToken(userId, token);
    logger.info('FCM: notifications set up successfully');
    return true;
  } catch (error) {
    logger.error('FCM: setup failed:', error);
    return false;
  }
};

/**
 * Subscribe to foreground messages (app is open and focused).
 * Returns an unsubscribe function.
 */
export const onForegroundMessage = (callback) => {
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
};
